
import { defineComponent, ref, computed, watch, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import ApiService from "@/core/services/ApiService";
import IndicatorGroups from "./IndicatorGroups.vue";
import {
  CaseDetail,
  TimelineEntry,
  renderMarkdown,
  formatDate,
  formatDateTime,
  normalizeCriticity,
  criticityColor,
  indicatorCount,
  entityQueryString,
} from "./trf";

interface TimelineItem {
  entry: TimelineEntry;
  isFirst: boolean;
  indicatorCount: number;
  showIndicators: boolean;
}

export default defineComponent({
  name: "quimerax-case-detail",
  components: { IndicatorGroups },
  setup() {
    const route = useRoute();
    const router = useRouter();

    const detail = ref<CaseDetail | null>(null);
    const isLoading = ref(false);
    const notFound = ref(false);
    const showAllIndicators = ref(false);

    // Timeline: API returns oldest-first. Tag isFirst in the ORIGINAL order,
    // then reverse for newest-first display.
    const timelineItems = computed<TimelineItem[]>(() => {
      if (!detail.value) return [];
      return detail.value.timeline
        .map((entry, i) => ({
          entry,
          isFirst: i === 0,
          indicatorCount: indicatorCount(entry.indicators),
          showIndicators: false,
        }))
        .reverse();
    });

    // Only show the published range when both ends are present.
    const publishedRange = computed(() => {
      const s = detail.value?.stats;
      if (!s?.first_published_at || !s?.last_published_at) return null;
      return `${formatDate(s.first_published_at)} – ${formatDate(s.last_published_at)}`;
    });

    const goToFeed = (field: string, value: string) => {
      router.push({ path: "/apps/quimerax-news", query: { q: entityQueryString(field, value) } });
    };

    const fetchCase = async () => {
      const uuid = String(route.params.uuid ?? "");
      if (!uuid) return;
      isLoading.value = true;
      notFound.value = false;
      detail.value = null;
      try {
        const res = await ApiService.get(`/quimerax-news/case/${encodeURIComponent(uuid)}`);
        if (res.data?.error || res.data?.challenge_required) {
          notFound.value = true;
        } else {
          detail.value = res.data;
        }
      } catch (e) {
        notFound.value = true;
      } finally {
        isLoading.value = false;
      }
    };

    watch(() => route.params.uuid, fetchCase);
    onMounted(fetchCase);

    return {
      detail,
      isLoading,
      notFound,
      showAllIndicators,
      timelineItems,
      publishedRange,
      goToFeed,
      renderMarkdown,
      formatDate,
      formatDateTime,
      normalizeCriticity,
      criticityColor,
    };
  },
});
